import React, {
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  PropsWithChildren,
  useContext,
  HTMLAttributes,
  useReducer
} from 'react';
import regeneratorRuntime from 'regenerator-runtime';
import { styled } from '@mui/material/styles';
import CircularProgress from '@mui/material/CircularProgress';

import PauseCircleOutlineOutlinedIcon from '@mui/icons-material/PauseCircleOutlineOutlined';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';

import CrossIcon from '@/components/icons/Cross';
import UploadIcon from '@/components/icons/Upload';

import TableList, { ColumnMeta } from '@/components/TableList';

import { showMessage } from '@/actions/common';
import { Container, Button } from '@/components';

import { ProjectData } from '@/state/types/project-data';

import { ModelUpload, UploadStatus } from '@/state/types/model-upload';
import { ModelData } from '@/state/types/model-data';
import SupportedRevitVersions from './SupportedRevitVersions';
import hinted from '@/components/Hinted';
import dropzone from '@/components/Dropzone';
import t from '@/utils/translate';
import Memory from '@/components/Memory';
import BackgroundContext from '@/components/background/BackgroundContext';
import { QueuedUpload } from '@/components/background/Background.class';
import { Logger } from '@bimdata/client';

// @ts-ignore
window.regeneratorRuntime = regeneratorRuntime;

type UploaderProps = {
  project: ProjectData;
  files?: File[];
  onBusy?: () => void;
  onDone?: () => void;
  isBusy?: boolean;
} & PropsWithChildren;

const cellStyle = {
  paddingTop: '4px',
  paddingBottom: '4px',
  height: '40px',
};
const headerStyle = {
  paddingTop: '6px',
  paddingBottom: '6px',
};

const UploadInfo = styled('p')((props: { invert?: boolean }) => ({
  margin: 0,
  textAlign: 'center',
  fontSize: '13px',
  color: !!props.invert ? '#DFE1E6' : '#091E42',
}));

const Comment = styled('em')((props: { invert?: boolean }) => ({
  color: !!props.invert ? '#C1C7D0' : '#6B778C',
  display: 'block',
  fontStyle: 'normal'
}));

const Banner = styled('div')({
  border: '1px dashed #C1C7D0',
  padding: '30px 100px',
  display:'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '20px',
  flexGrow: 1,

  '&>p>em': { margin: '10px 0 0 0' },
});

const DragBanner = styled(Banner)({
  position: 'absolute',
  inset: '0',
  zIndex: 10,
  background: '#42526ECC',
});

const FilesListContainer = styled('div')({
  position: 'relative',
  flexGrow: 1,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  gap: '14px',
  minHeight: 0,

  '&>p': {
    textAlign: 'left',
    '&>em': {
      margin: '4px 0 0 0',
    }
  }
});

const UploaderActionBar = styled(Container)(({ theme }) => `
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: space-between;
  padding: 0;
`);

const ClearButton = styled(Button)(() => ({
  padding: '0px',
  borderRadius: '0px',
  minWidth: 'unset',
  width: '32px',
  height: '32px',
  fontSize: '16px',
  color: '#091E42',
  opacity: 0.2
}));

const StatusHinter = hinted(
  styled('div')({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '& svg': {
      fontSize: '20px',
    }
  }),
  { placement: 'bottom-end', delay: 250, fadeDuration: 50 }
);

const NameCell = styled('div')({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: '10px',
});
const FileNameBlock = styled('div')({
  flex: '1 1 auto',
  textOverflow: 'ellipsis',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  minWidth: 0,
});
const OverwriteButtonBlock = styled('div')({
  flexShrink: 0,
  flexGrow: 0,
});

const ContainerWithDropzone = dropzone<HTMLAttributes<HTMLDivElement>>(styled(Container)({
  minHeight: 395,
  padding: '20px',
  gap: '14px',
  width: '600px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
}));

const accepted = ['.bim', '.rvt'];

function checkUploadName(project: ProjectData, u: ModelUpload): ModelUpload {
  if (Array.isArray(project.models)) {
    let withSameName: ModelData | undefined;
    let i = 0;
    let section: string | null | undefined;
    while (!withSameName && i < project.models.length) {
      withSameName = project.models[i].models.find((m) => m.name == u.name.replace(/\.[^\s\.]+$/, ''));
      if (withSameName && project.models[i].name != null) {
        section = project.models[i].name;
      }
      i++;
    }
    if (withSameName) {
      u.status = UploadStatus.BLOCKED;
      if (section) {
        u.section = section;
      }
      u.error = new Error(t('model.upload.error.modelExists', { name: withSameName.name }));
    }
  }
  return u;
}


export default ({ project, onBusy, onDone, files, children }: UploaderProps) => {
  const [, forceUpdate] = useReducer((x) => x < Number.MAX_SAFE_INTEGER ? x + 1 : 0, 0);
  
  const background = useContext(BackgroundContext);

  const queue = background.uploads.get(project.code) || [];

  const accept = useCallback((files: File[]) => {
    setDragIndicator(false);
    for (const file of files) {
      const existing = background.getModelUpload(project.code, file.name);
      if (existing && (existing.status === UploadStatus.INPROGRESS) && existing.abort) {
        existing.abort().catch((err) => Logger.warn(err));
      }
      const upload: ModelUpload = existing || {
        name: file.name,
        status: UploadStatus.PENDING,
        loaded: 0,
        total: file.size,
      };
      upload.file = file;
      upload.name = file.name;
      upload.status = UploadStatus.PENDING;
      delete upload.error;
      checkUploadName(project, upload);
      background.addModelUpload(project.code, upload);
    }
    forceUpdate();
  }, [project]);

  useEffect(() => {
    if (Array.isArray(files) && files.length) {
      accept(files);
    }
  }, [files]);

  useEffect(() => {
    background.addEventListener('uploadProgress', forceUpdate);
    return () => {
      background.cleanSuccessUploads();
      background.removeEventListener('uploadProgress', forceUpdate);
    }
  }, []);

  const send = useCallback(async () => {
    onBusy && onBusy();
    await background.send(project.code, (error) => {
      showMessage(error.message);
      forceUpdate();
    });
    forceUpdate();
    onDone && onDone();
  }, [project.code, onDone]);

  const uStatus = (u: QueuedUpload) => {
    switch (u.status) {
      case UploadStatus.PENDING:
        return <StatusHinter hintDisabled={true}>
          <PauseCircleOutlineOutlinedIcon color="disabled" sx={{ color: '#091E42', opacity: 0.3 }}/>
        </StatusHinter>;
      case UploadStatus.SCHEDULED:
        return <StatusHinter hint={t('model.hint.upload.scheduled')}>
          <ScheduleOutlinedIcon color="disabled"/>
        </StatusHinter>;
      case UploadStatus.SUCCESS:
        return <StatusHinter hint={t('model.hint.upload.success')}>
          <CheckCircleOutlinedIcon color="success" sx={{ color: '#00875A' }}/>
        </StatusHinter>;
      case UploadStatus.FAILED:
        return <StatusHinter hint={u.error?.message}>
          {u.canRetry
            ? <ReportProblemOutlinedIcon color="warning"/>
            : <ErrorOutlineOutlinedIcon color="error"/>
          }
        </StatusHinter>;
      case UploadStatus.INPROGRESS:
        return <StatusHinter hint={t('model.hint.upload.inprogress')}>
          <CircularProgress variant="determinate" value={Math.round((u.loaded || 0) * 100/u.total)} size={16} sx={{ verticalAlign: 'middle', width: '16px', height: '16px' }}/>
        </StatusHinter>;
      case UploadStatus.BLOCKED:
        return <StatusHinter hint={u.error?.message}>
          <ReportProblemOutlinedIcon color="warning"/>
        </StatusHinter>;
    }
  };

  const inputRef = useRef<HTMLInputElement>(null);
  const openFilesSelector = useCallback(() => {
    if (inputRef.current) {
      inputRef.current.click();
    }
  }, [inputRef.current]);

  const [ dragIndicator, setDragIndicator ] = useState<boolean>(false);

  const windowBodyRef = useRef<HTMLDivElement>(null);

  const deleteUpload = useCallback((item: ModelUpload) => {
    if (windowBodyRef.current) {
      windowBodyRef.current.style.minHeight = `${windowBodyRef.current.offsetHeight}px`;
    }
    background.deleteUpload(project.code, item);
    forceUpdate();
  }, [ project.code, windowBodyRef.current ]);

  const action = useCallback((item: ModelUpload) => {
    item.status === UploadStatus.BLOCKED
      ? background.forceUpload(project.code, item)
      : background.retryUpload(project.code, item);
    forceUpdate();
  }, [ project.code ]);

  const columns = useMemo<ColumnMeta[]>(() => {
    return [
      {
        width: 40,
        attrName: 'status',
        noSort: true,
        dataGetter: (item: QueuedUpload) => uStatus(item),
        type: 'iconic',
        cellStyle,
        headerStyle,
      },
      {
        attrName: 'name',
        title: t('model.column.name'),
        cellStyle,
        headerStyle,
        noSort: true,
        dataGetter: (item: QueuedUpload) => (<NameCell>
          <FileNameBlock>{item.name}</FileNameBlock>
          <OverwriteButtonBlock>
            {(item.status === UploadStatus.BLOCKED ||
            (item.status === UploadStatus.FAILED && item.canRetry)) && (
              <Button
                type="button"
                variant="contained"
                size="medium"
                sx={{ width: '100%' }}
                onClick={() => { action(item); }}
              >
                {item.status === UploadStatus.BLOCKED ? t('action.overwrite') : t('action.repeat')}
              </Button>
            )}
          </OverwriteButtonBlock>
        </NameCell>),
      },
      {
        attrName: 'size',
        title: t('model.column.size'),
        width: 80,
        cellStyle: {
          ...cellStyle,
          paddingLeft: '8px',
        },
        headerStyle,
        noSort: true,
        dataGetter: (item: QueuedUpload) => {
          return <Memory value={item.file?.size || 0} />
        },
      },
      {
        width: 48,
        type: 'iconic',
        noSort: true,
        cellStyle: {
          ...cellStyle,
          textAlign: 'center',
        },
        headerStyle,
        dataGetter: (item: QueuedUpload) => {
          return <ClearButton
            onClick={() => deleteUpload(item)}
          >
            <CrossIcon/>
          </ClearButton>
        }
      },
    ];
  }, [ deleteUpload, action ]);

  return (
    <>
      <ContainerWithDropzone
        accept={accepted}
        onFileDrop={accept}
        noClick={true}
        inputRef={inputRef}
        onDragEnter={() => setDragIndicator(true)}
        onDragLeave={() => setDragIndicator(false)}
        ref={windowBodyRef}
      >
        {children}

        {!queue.length && (
          <Banner
            onClick={openFilesSelector}
            style={{
              minHeight: '276px',
              ...(dragIndicator ? {
                background: '#6B778C',
              } : {}),
            }}
          >
            <UploadIcon sx={{
              fontSize: '97px',
              fill: '#DFE1E6'
            }}/>
            <UploadInfo invert={dragIndicator}>
              {dragIndicator
                ? t('model.upload.hint.inDrag', true)
                : t('model.upload.hint.drag', true)
              }
              <Comment invert={dragIndicator}>
                <SupportedRevitVersions />
              </Comment>
            </UploadInfo>
          </Banner>
        )}

        {!!queue.length && (
          <FilesListContainer>

            <TableList<QueuedUpload>
              columns={columns}
              data={queue}
              style={{ height: 'auto' }}
            />
            <UploadInfo>
              {t('model.upload.hint.dragToAdd', true)}
              <Comment><SupportedRevitVersions /></Comment>
            </UploadInfo>

            {dragIndicator && (
              <DragBanner>
                <UploadIcon sx={{
                  fontSize: '97px',
                  fill: '#DFE1E6'
                }}/>
                <UploadInfo invert={true}>
                  {t('model.upload.hint.inDrag', true)}
                  <Comment invert={true}>
                    <SupportedRevitVersions />
                  </Comment>
                </UploadInfo>
              </DragBanner>
            )}

          </FilesListContainer>
        )}

        <UploaderActionBar>
          <Button
            type="button"
            variant="outlined"
            size="medium"
            onClick={openFilesSelector}
            sx={{
              border: '1px solid #6B778C',
              color: '#6B778C',
              '&:hover': {
                borderColor: '#6B778C',
              }
            }}
          >
            {!queue.length
              ? t('action.files.select')
              : t('action.files.add')
            }
          </Button>
          <Button
            type="button"
            variant="contained"
            size="medium"
            disabled={!queue.filter(({ status }) => status == UploadStatus.PENDING).length}
            onClick={send}
          >
            {t('model.action.upload')}
          </Button>
        </UploaderActionBar>
      </ContainerWithDropzone>
    </>
  );
}
